/**
 * CustomerVitals.jsx — Iter 168 (Danny)
 *
 * Left-rail vitals card on the Customer Focus page. Mirrors the
 * structure of LeadFocusVitals but the sections are tuned for
 * customer/account data:
 *   - Practice (company, specialty, NPI, website, group size)
 *   - Primary contact (name, title, email, phone)
 *   - Enrolled products (product · status · MRR · patients)
 *   - Implementation (specialist, kickoff, ETA, account status)
 *   - Locations (address chips)
 *
 * Every field is inline-editable via PUT /api/contacts/{id}.
 * Enrollments are READ-ONLY in this rail (full editor lives in the
 * Enrollments tab via ContactCard).
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Check, X, Building2, MapPin, Package } from "lucide-react";

/** Iter 168 — defensive error-message normalisation. FastAPI returns 422
 *  errors with `detail` as an ARRAY of {type, loc, msg, input, url} objects.
 *  Passing that array straight to `toast.error` (a Sonner ReactNode-typed
 *  prop) causes React to throw "Objects are not valid as a React child"
 *  and unmounts the entire page. Always run untrusted error payloads
 *  through this picker before rendering. */
function _pickErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d?.msg || JSON.stringify(d)).join("; ");
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return String(detail);
}

const fmtCompactCurrency = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return `$${Number(n).toLocaleString()}`;
};

const ENROLL_STATUS_COLORS = {
  active:  { bg: "var(--success-soft)", color: "#065f46", border: "#bbf7d0" },
  live:    { bg: "var(--success-soft)", color: "#065f46", border: "#bbf7d0" },
  pending: { bg: "#fef3c7", color: "#92400e", border: "#fde68a" },
  paused:  { bg: "#fee2e2", color: "#7f1d1d", border: "#fecaca" },
  churned: { bg: "#e5e7eb", color: "#374151", border: "#9ca3af" },
};

function EditableRow({ label, value, onSave, mono, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const save = () => {
    setEditing(false);
    if (draft !== value) onSave?.(draft);
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "100px 1fr auto", gap: 10, alignItems: "start", padding: "6px 0" }}>
      <span style={{ fontSize: 11, color: "var(--text-3)", paddingTop: 1 }}>{label}</span>
      {editing ? (
        <input
          autoFocus value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setDraft(value); setEditing(false); } }}
          className={mono ? "input num" : "input"}
          style={{ fontSize: 12, padding: "4px 8px" }}
          data-testid={`${testid}-input`}
        />
      ) : (
        <span
          className={mono ? "num" : ""}
          style={{ fontSize: 12, color: "var(--text-2)", fontWeight: 500, lineHeight: 1.4, whiteSpace: "pre-wrap" }}
          data-testid={testid}>
          {value || "—"}
        </span>
      )}
      {!editing && (
        <button
          onClick={() => { setDraft(value ?? ""); setEditing(true); }}
          data-testid={`${testid}-edit`}
          style={{ background: "none", border: "none", padding: 2, color: "var(--text-4)", cursor: "pointer" }}
          aria-label={`Edit ${label}`}>
          <Pencil style={{ width: 11, height: 11 }} />
        </button>
      )}
    </div>
  );
}

function Section({ eyebrow, children, right, testid }) {
  return (
    <div data-testid={testid} style={{ borderBottom: "0.5px solid var(--border-soft)", padding: "14px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span className="eyebrow">{eyebrow}</span>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function CustomerVitals({
  contact,
  enrollments = [],
  catalog = {},
  implementation = {},
  specialistName = null,
  apiBase,
  token,
  onUpdated,
}) {
  const updateField = async (key, value) => {
    try {
      await axios.put(`${apiBase}/contacts/${contact.id}`, { [key]: value }, { headers: { Authorization: `Bearer ${token}` } });
      onUpdated?.({ [key]: value });
      toast.success("Saved");
    } catch (e) {
      toast.error(_pickErrorMessage(e) || "Couldn't save");
    }
  };

  const accountStatus = (contact?.account_status || "").toLowerCase();
  const accountStatusLabel = accountStatus ? accountStatus.charAt(0).toUpperCase() + accountStatus.slice(1) : "—";

  const activeEnrollments = enrollments.filter(e => ["active", "live"].includes((e.status || "").toLowerCase()));
  const pendingEnrollments = enrollments.filter(e => !["active", "live"].includes((e.status || "").toLowerCase()));
  const allShown = [...activeEnrollments, ...pendingEnrollments];

  return (
    <div className="card" style={{ padding: 0, borderRadius: "var(--radius-lg)", overflow: "hidden" }}>
      <div style={{ padding: "14px 18px 12px", borderBottom: "0.5px solid var(--border-soft)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 600, color: "var(--navy)" }}>Customer vitals</span>
          <span style={{ fontSize: 10, fontWeight: 500, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>From CRM</span>
        </div>
      </div>

      <Section eyebrow="Practice" testid="customer-vitals-practice">
        <EditableRow label="Company" value={contact?.company} onSave={(v) => updateField("company", v)} testid="vital-company" />
        <EditableRow label="Specialty" value={contact?.specialty} onSave={(v) => updateField("specialty", v)} testid="vital-specialty" />
        <EditableRow label="NPI" value={contact?.npi} onSave={(v) => updateField("npi", v)} mono testid="vital-npi" />
        <EditableRow label="Website" value={contact?.website} onSave={(v) => updateField("website", v)} testid="vital-website" />
        <EditableRow label="Group size" value={contact?.group_size != null ? String(contact.group_size) : ""} onSave={(v) => updateField("group_size", Number(v) || null)} mono testid="vital-group-size" />
        <EditableRow label="Patients" value={contact?.num_patients != null ? String(contact.num_patients) : ""} onSave={(v) => updateField("num_patients", Number(v) || null)} mono testid="vital-num-patients" />
      </Section>

      <Section eyebrow="Primary contact" testid="customer-vitals-contact">
        <EditableRow label="First name" value={contact?.first_name} onSave={(v) => updateField("first_name", v)} testid="vital-first-name" />
        <EditableRow label="Last name" value={contact?.last_name} onSave={(v) => updateField("last_name", v)} testid="vital-last-name" />
        <EditableRow label="Title" value={contact?.job_title || contact?.role} onSave={(v) => updateField("job_title", v)} testid="vital-job-title" />
        <EditableRow label="Email" value={contact?.email} onSave={(v) => updateField("email", v)} testid="vital-email" />
        <EditableRow label="Phone" value={contact?.phone} onSave={(v) => updateField("phone", v)} mono testid="vital-phone" />
      </Section>

      <Section
        eyebrow="Enrolled products"
        right={<span style={{ fontSize: 10, color: "var(--text-3)" }}>{allShown.length} total</span>}
        testid="customer-vitals-enrollments">
        {allShown.length === 0 ? (
          <div style={{ fontSize: 11, color: "var(--text-3)", fontStyle: "italic", padding: "6px 0" }}>
            No product enrollments yet. Use the Enrollments tab to add one.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {allShown.map((e) => {
              const def = catalog[e.product];
              const label = def?.label || e.product;
              const c = ENROLL_STATUS_COLORS[(e.status || "").toLowerCase()] || ENROLL_STATUS_COLORS.pending;
              return (
                <div
                  key={e.id}
                  data-testid={`vital-enrollment-${e.product}`}
                  style={{ padding: "8px 10px", borderRadius: 8, background: "var(--paper)", border: "0.5px solid var(--border-soft)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)" }}>
                      <Package style={{ width: 11, height: 11, color: "var(--text-3)", marginRight: 5, verticalAlign: "middle" }} />
                      {label}
                    </span>
                    <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 999, fontWeight: 600, background: c.bg, color: c.color, border: `0.5px solid ${c.border}`, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      {e.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {e.monthly_value > 0 && <span className="num"><strong style={{ color: "var(--text-2)" }}>{fmtCompactCurrency(e.monthly_value)}</strong>/mo</span>}
                    {e.patient_count > 0 && <span className="num">{e.patient_count.toLocaleString()} patients</span>}
                    {e.started_at && <span className="num">since {new Date(e.started_at).toLocaleDateString(undefined, { month: "short", year: "2-digit" })}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <Section eyebrow="Implementation" testid="customer-vitals-implementation">
        <EditableRow label="Specialist" value={specialistName} onSave={() => {}} testid="vital-specialist" />
        <EditableRow
          label="Kickoff"
          value={implementation?.kickoff_at ? new Date(implementation.kickoff_at).toLocaleDateString() : ""}
          onSave={() => {}}
          testid="vital-kickoff" />
        <EditableRow
          label="Est. go-live"
          value={implementation?.estimated_go_live ? new Date(implementation.estimated_go_live).toLocaleDateString() : ""}
          onSave={() => {}}
          testid="vital-go-live" />
        <EditableRow label="Account status" value={accountStatusLabel} onSave={(v) => updateField("account_status", (v || "").toLowerCase())} testid="vital-account-status" />
        <EditableRow label="Source" value={contact?.source} onSave={(v) => updateField("source", v)} testid="vital-source" />
        <div style={{ fontSize: 10, color: "var(--text-4)", marginTop: 4, fontStyle: "italic" }}>
          Edit kickoff / ETA / specialist on the Implementation tab.
        </div>
      </Section>

      {(contact?.address || contact?.city || contact?.state) && (
        <Section eyebrow="Location" testid="customer-vitals-location">
          <div style={{ padding: "8px 10px", borderRadius: 8, background: "var(--paper)", border: "0.5px solid var(--border-soft)", display: "flex", gap: 8 }}>
            <MapPin style={{ width: 12, height: 12, color: "var(--text-3)", flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 11, color: "var(--text-2)", lineHeight: 1.5 }}>
              {contact?.address && <div>{contact.address}</div>}
              <div>{[contact?.city, contact?.state, contact?.zip].filter(Boolean).join(", ") || "—"}</div>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}
